import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FileText,
  Download,
  Search,
  Calendar,
  ArrowUpRight,
  TrendingUp,
  Wallet,
  Clock,
  ChartBarIcon
} from "lucide-react";
import api from '../services/api';
import { payrollAPI, employeePayslipAPI } from '../services/employee.service';
import ActivityLogs from '../components/ActivityLogs';
import { useAuditLog } from '../hooks/useAuditLog';

const PayrollReports = () => {
  const [activeTab, setActiveTab] = useState('reports');
  const [reports, setReports] = useState({
    summary: { total_gross: 0, total_net: 0, total_tax: 0 },
    employees: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    employee_name: "",
    period_type: "monthly",
    start_date: "",
    end_date: ""
  });

  // Activity types for payroll reports
  const activityTypes = [
    "Payroll Report Generated", "Bulk Report Downloaded", "Employee Report Filtered", 
    "Monthly Report Created", "Quarterly Report Generated", "Annual Report Exported",
    "Payroll Data Analyzed", "Report Period Changed", "Employee Search Performed"
  ];

  // Audit logging hook
  const { logAction } = useAuditLog('Payroll Reports');

  // Prevent state updates on unmounted component
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // --------------------------------------------------
  // FETCH PAYROLL DATA (ORIGINAL WORKING VERSION)
  // --------------------------------------------------
  const fetchPayrollData = useCallback(async () => {
    console.log('🔍 Starting fetchPayrollData...');
    setLoading(true);
    setError(null);

    try {
      console.log('📡 Making API call to /payroll-bulk/generated-payslips');
      const bulkResponse = await api.get('/payroll-bulk/generated-payslips');
      console.log('📊 API Response:', bulkResponse);
      
      // Check both possible response structures
      const payslips = bulkResponse?.data?.payslips || bulkResponse?.payslips || [];
      console.log('💼 Extracted payslips:', payslips.length, 'records');
      
      if (payslips && payslips.length > 0) {
        console.log('✅ Found payslips data, processing...');
        
        // Filter employees based on search
        const filteredEmployees = payslips.filter(emp => 
          !filters.employee_name || 
          emp.employee_name?.toLowerCase().includes(filters.employee_name.toLowerCase()) ||
          emp.emp_code?.toLowerCase().includes(filters.employee_name.toLowerCase())
        );
        
        console.log('🔍 Filtered employees:', filteredEmployees.length);
        
        // Calculate summary
        const summary = filteredEmployees.reduce((acc, emp) => {
          acc.total_gross += Number(emp.gross_salary) || 0;
          acc.total_net += Number(emp.net_salary) || 0;
          acc.total_tax += Number(emp.tds) || 0;
          return acc;
        }, { total_gross: 0, total_net: 0, total_tax: 0 });
        
        console.log('💰 Calculated summary:', summary);

        const processedEmployees = filteredEmployees.map(emp => ({
          emp_code: emp.emp_code,
          name: emp.employee_name,
          basic: emp.basic_salary || 0,
          hra: emp.hra || 0,
          special_allowance: emp.special_allowance || 0,
          variable_pay: emp.variable_pay || 0,
          pf: emp.pf_deduction || 0,
          pt: emp.professional_tax || 0,
          tds: emp.tds || 0,
          lop: emp.lop || 0,
          other_deductions: emp.other_deductions || 0,
          gross_pay: emp.gross_salary || 0,
          gross_deductions: emp.total_deductions || 0,
          net_pay: emp.net_salary || 0
        }));
        
        console.log('📋 Processed employees for display:', processedEmployees.length);
        
        setReports({ summary, employees: processedEmployees });
        logAction('Payroll Report Generated', `Generated report for ${filteredEmployees.length} employees`);
        console.log('✅ Data set successfully!');
      } else {
        console.log('⚠️ No payslips data found');
        setReports({
          summary: { total_gross: 0, total_net: 0, total_tax: 0 },
          employees: []
        });
      }
    } catch (err) {
      console.error("🚫 Error fetching payroll data:", err);
      setError("Unable to fetch payroll data. Please ensure payroll has been generated in Salary Structure Builder.");
      setReports({
        summary: { total_gross: 0, total_net: 0, total_tax: 0 },
        employees: []
      });
    } finally {
      console.log('🏁 fetchPayrollData completed, setting loading to false');
      setLoading(false);
    }
  }, [filters.employee_name, filters.period_type, isMounted, logAction]);

  // --------------------------------------------------
  // DOWNLOAD HANDLER (SAFE)
  // --------------------------------------------------
  const handleDownload = async (type) => {
    try {
      // Create CSV data for Excel export
      if (type === 'excel') {
        const csvData = [
          ['S.No', 'Employee', 'Emp ID', 'Basic Salary', 'HRA', 'Special Allowance', 'Variable Pay', 'PF', 'Professional Tax', 'Income Tax', 'Loss of Pay', 'Other Deductions', 'Gross Pay', 'Gross Deductions', 'Net Pay'],
          ...reports.employees.map((emp, index) => [
            index + 1,
            emp.name,
            emp.emp_code,
            emp.basic,
            emp.hra,
            emp.special_allowance,
            emp.variable_pay,
            emp.pf,
            emp.pt,
            emp.tds,
            emp.lop,
            emp.other_deductions,
            emp.gross_pay,
            emp.gross_deductions,
            emp.net_pay
          ])
        ];
        
        const csvContent = csvData.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payroll-report-${filters.period_type}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        logAction('Report Downloaded', `Excel report downloaded with ${reports.employees.length} employees`);
      }
      
      // For PDF, create a simple HTML report
      if (type === 'pdf') {
        const printWindow = window.open('', '_blank');
        const htmlContent = `
          <html>
            <head><title>Payroll Report</title></head>
            <body>
              <h1>Payroll Report - ${filters.period_type.toUpperCase()}</h1>
              <table border="1" style="border-collapse: collapse; width: 100%;">
                <tr>
                  <th>S.No</th><th>Employee</th><th>Emp ID</th><th>Basic</th><th>HRA</th><th>Net Pay</th>
                </tr>
                ${reports.employees.map((emp, index) => `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${emp.name}</td>
                    <td>${emp.emp_code}</td>
                    <td>₹${emp.basic}</td>
                    <td>₹${emp.hra}</td>
                    <td>₹${emp.net_pay}</td>
                  </tr>
                `).join('')}
              </table>
            </body>
          </html>
        `;
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.print();
        
        logAction('Report Downloaded', `PDF report generated with ${reports.employees.length} employees`);
      }
    } catch (err) {
      console.error(`Error downloading ${type}:`, err);
    }
  };

  // Auto-fetch data when filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPayrollData();
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [fetchPayrollData]);

  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0
    }).format(Number(val) || 0);

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50 p-6" style={{ fontFamily: 'Roboto, sans-serif' }}>
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>Payroll Reports & Analytics</h1>
              <p className="text-gray-600 mt-1">Comprehensive payroll analysis and reporting system</p>
            </div>
            
            {/* Tab Navigation - Top Right */}
            <nav className="flex space-x-4">
              {[
                { id: 'reports', label: 'Reports' },
                { id: 'activity', label: 'Activity' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-3 border-b-2 font-medium text-xs flex items-center gap-1 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.id === 'activity' && <Clock size={14} />}
                  {tab.id === 'reports' && <ChartBarIcon size={14} />}
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Activity Logs Tab */}
            {activeTab === 'activity' && (
              <ActivityLogs 
                moduleName="Payroll Reports" 
                activityTypes={activityTypes} 
              />
            )}
            
            {/* Reports Tab */}
            {activeTab === 'reports' && (
              <div>
                {/* Controls */}
                <div className="bg-white p-6 rounded-xl border shadow-sm mb-6">
                  <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                    <div className="flex flex-col sm:flex-row gap-4 flex-1">
                      <div className="relative min-w-[280px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                          type="text"
                          placeholder="Search by employee name or ID..."
                          className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={filters.employee_name}
                          onChange={(e) => setFilters({ ...filters, employee_name: e.target.value })}
                        />
                      </div>

                      <select
                        className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[140px]"
                        value={filters.period_type}
                        onChange={(e) => setFilters({ ...filters, period_type: e.target.value })}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                        <option value="custom">Custom Range</option>
                      </select>

                      {filters.period_type === 'custom' && (
                        <div className="flex gap-3">
                          <input
                            type="date"
                            className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            value={filters.start_date}
                            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                          />
                          <input
                            type="date"
                            className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            value={filters.end_date}
                            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => handleDownload("pdf")} 
                        className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                      >
                        <FileText size={16} /> Export PDF
                      </button>
                      <button 
                        onClick={() => handleDownload("excel")} 
                        className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                      >
                        <Download size={16} /> Export Excel
                      </button>
                    </div>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <SummaryCard 
                    title="Total Gross Pay" 
                    amount={reports.summary.total_gross} 
                    icon={<TrendingUp className="text-blue-600" />} 
                    bgColor="bg-blue-50" 
                    textColor="text-blue-600"
                  />
                  <SummaryCard 
                    title="Total Net Pay" 
                    amount={reports.summary.total_net} 
                    icon={<Wallet className="text-green-600" />} 
                    bgColor="bg-green-50" 
                    textColor="text-green-600"
                  />
                  <SummaryCard 
                    title="Total Deductions" 
                    amount={reports.summary.total_tax} 
                    icon={<ArrowUpRight className="text-orange-600" />} 
                    bgColor="bg-orange-50" 
                    textColor="text-orange-600"
                  />
                </div>

                {/* Download Buttons */}

                {error && <div className="mb-4 text-red-600 font-medium">{error}</div>}

                {/* Data Table */}
                <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left table-auto">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr className="text-center text-sm font-semibold text-gray-700">
                          <th colSpan="4" className="py-4 px-6 border-r border-gray-200">Employee Details</th>
                          <th colSpan="4" className="py-4 px-6 bg-blue-50 text-blue-700 border-r border-gray-200">Earnings</th>
                          <th colSpan="5" className="py-4 px-6 bg-red-50 text-red-700 border-r border-gray-200">Deductions</th>
                          <th colSpan="3" className="py-4 px-6 bg-green-50 text-green-700">Summary</th>
                        </tr>
                        <tr className="text-xs font-medium text-gray-600 bg-gray-50">
                          <th className="py-3 px-4 text-center border-r border-gray-200">S.No</th>
                          <th className="py-3 px-4 text-center border-r border-gray-200">Period</th>
                          <th className="py-3 px-4 text-left border-r border-gray-200">Employee Name</th>
                          <th className="py-3 px-4 text-left border-r border-gray-200">Employee ID</th>
                          <th className="py-3 px-4 text-right border-r border-gray-200">Basic</th>
                          <th className="py-3 px-4 text-right border-r border-gray-200">HRA</th>
                          <th className="py-3 px-4 text-right border-r border-gray-200">Special Allow.</th>
                          <th className="py-3 px-4 text-right border-r border-gray-200">Variable Pay</th>
                          <th className="py-3 px-4 text-right border-r border-gray-200">PF</th>
                          <th className="py-3 px-4 text-right border-r border-gray-200">Prof. Tax</th>
                          <th className="py-3 px-4 text-right border-r border-gray-200">TDS</th>
                          <th className="py-3 px-4 text-right border-r border-gray-200">LOP</th>
                          <th className="py-3 px-4 text-right border-r border-gray-200">Other Ded.</th>
                          <th className="py-3 px-4 text-right border-r border-gray-200">Gross Pay</th>
                          <th className="py-3 px-4 text-right border-r border-gray-200">Total Ded.</th>
                          <th className="py-3 px-4 text-right font-semibold">Net Pay</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {loading ? (
                          <tr>
                            <td colSpan="16" className="text-center py-12 text-gray-500">
                              <div className="flex items-center justify-center gap-2">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                Loading payroll data...
                              </div>
                            </td>
                          </tr>
                        ) : reports.employees.length > 0 ? (
                          reports.employees.map((emp, index) => (
                            <tr key={emp.emp_code || index} className="hover:bg-gray-50 transition-colors">
                              <td className="py-3 px-4 text-center text-sm font-medium text-gray-900">{index + 1}</td>
                              <td className="py-3 px-4 text-center text-sm text-gray-600 capitalize">{filters.period_type}</td>
                              <td className="py-3 px-4 text-sm font-medium text-gray-900">{emp.name || 'N/A'}</td>
                              <td className="py-3 px-4 text-sm text-gray-600 font-mono">{emp.emp_code || "-"}</td>
                              <td className="py-3 px-4 text-right text-sm text-gray-900">{formatCurrency(emp.basic)}</td>
                              <td className="py-3 px-4 text-right text-sm text-gray-900">{formatCurrency(emp.hra)}</td>
                              <td className="py-3 px-4 text-right text-sm text-gray-900">{formatCurrency(emp.special_allowance)}</td>
                              <td className="py-3 px-4 text-right text-sm text-gray-900">{formatCurrency(emp.variable_pay)}</td>
                              <td className="py-3 px-4 text-right text-sm text-red-600">{formatCurrency(emp.pf)}</td>
                              <td className="py-3 px-4 text-right text-sm text-red-600">{formatCurrency(emp.pt)}</td>
                              <td className="py-3 px-4 text-right text-sm text-red-600">{formatCurrency(emp.tds)}</td>
                              <td className="py-3 px-4 text-right text-sm text-red-600">{formatCurrency(emp.lop)}</td>
                              <td className="py-3 px-4 text-right text-sm text-red-600">{formatCurrency(emp.other_deductions)}</td>
                              <td className="py-3 px-4 text-right text-sm font-semibold text-blue-600">{formatCurrency(emp.gross_pay)}</td>
                              <td className="py-3 px-4 text-right text-sm font-semibold text-red-600">{formatCurrency(emp.gross_deductions)}</td>
                              <td className="py-3 px-4 text-right text-sm font-bold text-green-600">{formatCurrency(emp.net_pay)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="16" className="text-center py-12 text-gray-500">
                              <div className="flex flex-col items-center gap-2">
                                <FileText className="h-8 w-8 text-gray-400" />
                                <span>No payroll data found</span>
                                <span className="text-sm">Try adjusting your search criteria</span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// --------------------------------------------------
// SUMMARY CARD
// --------------------------------------------------
const SummaryCard = ({ title, amount, icon, bgColor, textColor }) => (
  <div className="bg-white p-6 rounded-xl border shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
        <h3 className={`text-2xl font-bold ${textColor || 'text-gray-900'}`}>
          {new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0
          }).format(Number(amount) || 0)}
        </h3>
      </div>
      <div className={`p-3 rounded-lg ${bgColor}`}>
        {icon}
      </div>
    </div>
  </div>
);

export default PayrollReports;
